"use client";

import type { AssetRotatorBlock } from "@mw/shared/payload-types";

import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import { DesktopMobileImage } from "@ui/components/core/DesktopMobileImage";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { clsx } from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { type Key, Tab, TabList, TabPanel, Tabs } from "react-aria-components";

import { Block } from "./Block";

type ProgressIndicatorProps = {
  isActive: boolean;
  animationKey: string;
  durationSeconds: number;
  onComplete?: () => void;
  className?: string;
};

const ProgressIndicator = ({
  isActive,
  animationKey,
  durationSeconds,
  onComplete,
  className,
}: ProgressIndicatorProps) => {
  return (
    <div className={clsx("tw-relative tw-h-1 tw-rounded-md tw-overflow-hidden tw-bg-gray-200", className)}>
      <m.div
        key={animationKey}
        className='tw-absolute tw-inset-y-0 tw-left-0 tw-rounded-md tw-bg-black'
        initial={{ width: "0%" }}
        animate={{ width: isActive ? "100%" : "0%" }}
        transition={isActive ? { duration: durationSeconds, ease: "linear" } : { duration: 0 }}
        onAnimationComplete={() => {
          if (isActive && onComplete) {
            onComplete();
          }
        }}
      />
    </div>
  );
};

export const BlockAssetRotator = ({ assets, interval }: AssetRotatorBlock) => {
  const intervalSeconds = interval;
  const [selectedKey, setSelectedKey] = useState<Key>(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const assetItems = assets ?? [];
  const assetCount = assetItems.length;
  const activeIndex = Number(selectedKey);
  const activeAsset = assetItems[activeIndex];

  const advanceSlide = useCallback(() => {
    setSelectedKey(prev => (Number(prev) + 1) % assetCount);
    setAnimationKey(prev => prev + 1);
  }, [assetCount]);

  const handleSelectionChange = useCallback((key: Key) => {
    setSelectedKey(key);
    setAnimationKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const nowVisible = entry.isIntersecting;
        setIsVisible(nowVisible);
        if (nowVisible) {
          setAnimationKey(prev => prev + 1);
        }
      },
      { threshold: 0.1 },
    );

    const el = containerRef.current;
    if (el) observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (assetCount === 0) return null;

  return (
    <Block name='asset-rotator' className='tw-w-full tw-flex tw-flex-col tw-items-center'>
      <Tabs
        selectedKey={selectedKey}
        onSelectionChange={handleSelectionChange}
        className='tw-flex tw-flex-col tw-w-full'
      >
        {/* TabList rendered first in source order so React Aria registers tabs before panels */}
        <div className='tw-w-full tw-mt-6 tw-px-4 sm:tw-px-8 lg:tw-px-8 tw-order-2'>
          <TabList className='tw-flex tw-gap-2 md:tw-gap-10'>
            {assetItems.map((asset, index) => (
              <Tab
                key={asset.id ?? index}
                id={index}
                className='tw-flex-1 tw-flex tw-flex-col tw-items-start tw-text-left tw-cursor-pointer tw-outline-none focus-visible:tw-outline-focus tw-rounded-sm'
              >
                {({ isSelected }) => (
                  <>
                    <ProgressIndicator
                      isActive={isSelected && isVisible}
                      animationKey={`indicator-${index}-${animationKey}`}
                      durationSeconds={intervalSeconds}
                      onComplete={advanceSlide}
                      className='tw-w-full'
                    />
                    <p className='tw-hidden md:tw-block tw-mt-6 tw-type-xl lg:tw-type-2xl tw-text-black'>
                      {asset.title}
                    </p>
                    <p className='tw-hidden md:tw-block tw-mt-5 tw-type-sm lg:tw-type-base tw-text-gray-500'>
                      {asset.description}
                    </p>
                  </>
                )}
              </Tab>
            ))}
          </TabList>
        </div>

        <div ref={containerRef} className='tw-w-full tw-order-1'>
          <div className='tw-relative tw-isolate tw-overflow-hidden tw-rounded-3xl tw-bg-cream tw-pt-6 md:tw-pt-10 tw-pl-4 tw-pr-0 md:tw-px-10 lg:tw-px-16 xl:tw-px-36'>
            {/* Inner white card with shadow — image container */}
            <div className='tw-w-full tw-aspect-[3/2] md:tw-aspect-[11/6] tw-rounded-tl-2xl md:tw-rounded-t-2xl tw-overflow-hidden tw-relative'>
              {/* Crossfade with plus-lighter blend mode */}
              <div className='tw-absolute tw-inset-0' style={{ isolation: "isolate" }}>
                {assetItems.map((asset, index) => (
                  <TabPanel
                    key={asset.id ?? index}
                    id={index}
                    shouldForceMount
                    className='tw-absolute tw-inset-0 tw-outline-none'
                  >
                    {({ isInert }) => (
                      <m.div
                        className='tw-absolute tw-inset-0'
                        style={{ mixBlendMode: "plus-lighter" }}
                        animate={{ opacity: isInert ? 0 : 1 }}
                        initial={false}
                        transition={{ duration: 0.3 }}
                      >
                        {asset.type === "backgroundForegroundImage" && asset.backgroundForegroundImage && (
                          <DesktopMobileImage
                            imageConfig={asset.backgroundForegroundImage}
                            className='tw-object-cover'
                            mobileClassName='tw-object-left-top'
                          />
                        )}
                      </m.div>
                    )}
                  </TabPanel>
                ))}
              </div>
            </div>
            <HairlineCardBorder roundedClassName='tw-rounded-3xl' />
          </div>
        </div>

        {/* Titles — Mobile: one at a time with animation */}
        <div className='tw-w-full tw-mt-5 md:tw-hidden tw-px-4 sm:tw-px-8 tw-order-3' aria-hidden='true'>
          <div className='tw-relative tw-overflow-hidden'>
            <AnimatePresence mode='wait'>
              <m.div
                key={activeIndex}
                className='tw-flex tw-flex-col tw-gap-3'
                initial={{ opacity: 0, transform: "translateX(1.25rem)" }}
                animate={{ opacity: 1, transform: "translateX(0rem)" }}
                exit={{ opacity: 0, transform: "translateX(-1.25rem)" }}
                transition={{ duration: 0.25 }}
              >
                <p className='tw-type-2xl tw-text-black tw-py-1'>{activeAsset?.title}</p>
                <p className='tw-type-base tw-text-gray-500 tw-py-1'>{activeAsset?.description}</p>
              </m.div>
            </AnimatePresence>
          </div>
        </div>
      </Tabs>
    </Block>
  );
};

BlockAssetRotator.displayName = "BlockAssetRotator";
